import { recordTable } from "@/components/tables/table-presentation";
import { Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { MouseEventHandler } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import { useProjectsQueryScope } from "@/features/projects/hooks/useProjectsQuery";
import type { Project } from "@/features/projects/types";

type ApiKeysToolbarProps = {
  searchQuery: string;
  projectFilter: string;
  projects: Project[];
  disabled?: boolean;
  onSearchChange: (value: string) => void;
  onProjectFilterChange: (value: string) => void;
  onCreate: MouseEventHandler<HTMLButtonElement>;
};

const ApiKeysToolbar = ({
  searchQuery,
  projectFilter,
  projects,
  disabled = false,
  onSearchChange,
  onProjectFilterChange,
  onCreate,
}: ApiKeysToolbarProps) => {
  const { t } = useTranslation();
  const { scope } = useProjectsQueryScope();

  return (
    <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="relative w-full lg:w-[300px]">
        <Search
          aria-hidden="true"
          className={recordTable.searchIcon}
        />
        <Input
          type="search"
          value={searchQuery}
          disabled={disabled}
          aria-label={t("apiKeys.searchCurrentPage")}
          aria-describedby="api-keys-search-scope"
          placeholder={t("apiKeys.searchCurrentPage")}
          className={recordTable.search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <span id="api-keys-search-scope" className="sr-only">
          {t("apiKeys.searchCurrentPageDescription")}
        </span>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
        <ProjectCombobox
          scopeKey={JSON.stringify(scope)}
          options={projects}
          label={t("apiKeys.filterByProject")}
          allProjects={{ id: "all", name: t("apiKeys.allProjects") }}
          className="h-9 w-full bg-input shadow-xs sm:w-[200px]"
          value={projectFilter}
          disabled={disabled || projects.length === 0}
          onValueChange={onProjectFilterChange}
        />

        <Button
          type="button"
          className="h-9 rounded-lg px-4"
          disabled={disabled}
          onClick={onCreate}
        >
          <Plus aria-hidden="true" className="size-4" />
          {t("apiKeys.createNewKey")}
        </Button>
      </div>
    </div>
  );
};

export default ApiKeysToolbar;
